import type { MapId } from '../engine/types.js';
import { computeFragments, type PermanentUpgradeId } from '../config/balance.js';
import { MAP_DEFINITIONS } from '../config/maps.js';
import { ApiClient } from '../api/client.js';
import {
  loadProfile,
  saveProfile,
  applyRunFinish,
  type UserProfile,
} from './profile.js';
import { loadSettings, saveSettings, type GameSettings } from './settings.js';
import { GameController } from './GameController.js';
import { AudioManager } from '../audio/AudioManager.js';
import { el, button, clear } from '../ui/dom.js';
import { attachMenuBackground } from '../ui/menuBackground.js';
import {
  mountEmptyScreen,
  showAuthModal,
  showGameOverAdmireBar,
  showGameOverModal,
  showInGameSettings,
  showPauseMenu,
  showRewardModal,
  showTutorialToast,
  clearTutorial,
  removeModals,
} from '../ui/modals.js';
import { createGameHud, updateHud } from '../ui/gameHud.js';
import { renderShop } from '../ui/shop.js';

export type Screen =
  | 'menu'
  | 'maps'
  | 'shop'
  | 'leaderboard'
  | 'profile'
  | 'settings'
  | 'game';

export class App {
  root: HTMLElement;
  screen: Screen = 'menu';
  profile: UserProfile;
  settings: GameSettings;
  api: ApiClient;
  audio: AudioManager;
  private game: GameController | null = null;
  private hud: ReturnType<typeof createGameHud> | null = null;
  private menuBgStop: (() => void) | null = null;
  private selectedMap: MapId = 'core';
  private lbTab: 'score' | 'day' = 'score';
  /** Settled game-over awaiting results modal (admire phase). */
  private gameOverPending: { day: number; score: number; fragments: number } | null =
    null;

  constructor(mount: HTMLElement) {
    this.root = mount;
    this.profile = loadProfile();
    this.settings = loadSettings();
    this.api = new ApiClient(this.profile);
    this.audio = new AudioManager(this.settings);
    // Unlock WebAudio after first gesture (menu or game).
    const unlock = () => {
      this.audio.ensureStarted();
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    // App-level ESC (single listener) so pause menu never depends on a leaked controller.
    window.addEventListener(
      'keydown',
      (e) => {
        if (this.screen !== 'game') return;
        if (e.key !== 'Escape' && e.code !== 'Escape') return;
        e.preventDefault();
        this.openEscapeMenu();
      },
      true,
    );
    void this.api.trySilentSso().then(() => {
      this.profile = this.api.getProfile();
      if (this.screen === 'menu') this.render();
    });
    this.render();
  }

  navigate(to: Screen, opts?: { keepSave?: boolean }): void {
    if (this.game) {
      this.game.destroy(opts?.keepSave !== false);
      this.game = null;
      this.hud = null;
    }
    this.gameOverPending = null;
    removeModals(this.root);
    clearTutorial(this.root);
    this.screen = to;
    this.render();
  }

  /** Award fragments/score for the current run, clear save, leave the map fresh. */
  private settleRunAndLeave(to: Screen): void {
    if (this.game && !this.game.sim.state.gameOver) {
      this.awardRun(this.game.sim.state.day, this.game.sim.state.score);
    }
    GameController.clearAutosave();
    this.navigate(to, { keepSave: false });
  }

  private awardRun(day: number, score: number): { fragmentsEarned: number } {
    const mapId = this.game?.sim.state.mapId ?? this.selectedMap;
    const pulsesDelivered = this.game?.sim.state.pulsesDelivered ?? 0;
    const cablesBuilt = this.game?.sim.state.cables.length ?? 0;
    const { fragmentsEarned, profile } = applyRunFinish(this.profile, mapId, day, score);
    this.profile = profile;
    this.api.setProfile(profile);
    void this.api.finishRun({
      mapId,
      day,
      score,
      fragments: fragmentsEarned,
      pulsesDelivered,
      cablesBuilt,
    });
    return { fragmentsEarned };
  }

  private render(): void {
    if (this.menuBgStop) {
      this.menuBgStop();
      this.menuBgStop = null;
    }
    clear(this.root);

    switch (this.screen) {
      case 'menu':
        this.renderMenu();
        break;
      case 'maps':
        this.renderMaps();
        break;
      case 'shop':
        this.renderShopScreen();
        break;
      case 'leaderboard':
        void this.renderLeaderboard();
        break;
      case 'profile':
        this.renderProfile();
        break;
      case 'settings':
        this.renderSettings();
        break;
      case 'game':
        this.renderGame();
        break;
    }
  }

  private renderMenu(): void {
    const screen = mountEmptyScreen(this.root, 'menu-screen');
    const bg = el('canvas', 'menu-bg') as HTMLCanvasElement;
    screen.append(bg);
    this.menuBgStop = attachMenuBackground(bg);

    const content = el('div', 'menu-content');
    content.append(
      el('h1', 'logo', ['PULSE', el('span', '', ['NETWORK'])]),
      el('p', 'tagline', ['Synchronisez l\'énergie. Survivez au réseau.']),
    );

    if (this.profile.loggedIn && this.profile.username) {
      content.append(
        el('p', 'user-bar', [
          this.profile.username,
          ' · ',
          `${this.profile.fragments} fragments`,
        ]),
      );
    } else {
      const auth = el('div', 'auth-panel');
      auth.append(
        el('p', '', ['Connectez-vous pour sauvegarder progression et classements.']),
        button('Connexion / Inscription', 'btn primary', () => {
          showAuthModal(
            this.root,
            {
              login: (u, p) => this.api.login(u, p),
              register: (u, p, email) => this.api.register(u, p, email ?? ''),
              sso: () => {
                void this.api.ssoMusicGuess();
              },
            },
            () => {
              this.profile = this.api.getProfile();
              this.render();
            },
          );
        }),
        button('Continuer avec MusicGuess', 'btn accent', () => {
          void this.api.ssoMusicGuess();
        }),
      );
      content.append(auth);
    }

    const actions = el('div', 'menu-actions');
    const play = button('Jouer', 'btn primary large', () => this.navigate('maps'));
    actions.append(
      play,
      button('Boutique', 'btn large', () => this.navigate('shop')),
      button('Classement', 'btn large', () => this.navigate('leaderboard')),
      button('Profil', 'btn large', () => this.navigate('profile')),
      button('Réglages', 'btn large', () => this.navigate('settings')),
    );
    content.append(actions);

    const nav = el('nav', 'bottom-nav mobile-only');
    nav.append(
      button('▶', 'nav-btn', () => play.click()),
      button('🛒', 'nav-btn', () => this.navigate('shop')),
      button('⚙', 'nav-btn', () => this.navigate('settings')),
    );
    screen.append(content, nav);
  }

  private mapUnlocked(mapId: MapId): boolean {
    const def = MAP_DEFINITIONS[mapId];
    if (def.unlockOnMap === null) return true;
    const prevDay = this.profile.progressDays[def.unlockOnMap] ?? 0;
    return prevDay >= def.unlockRequirementDay;
  }

  private renderMaps(): void {
    const screen = mountEmptyScreen(this.root, 'maps-screen');
    screen.append(el('h1', 'screen-title', ['Choisir une carte']));
    const list = el('div', 'map-list');

    const names: Record<MapId, string> = {
      core: 'Noyau',
      ruins: 'Ruines',
      magnetic: 'Magnétique',
    };

    for (const id of ['core', 'ruins', 'magnetic'] as MapId[]) {
      const unlocked = this.mapUnlocked(id);
      const rec = this.profile.records[id];
      const card = el('div', `map-card ${unlocked ? '' : 'locked'}`);
      card.append(
        el('h3', '', [names[id]]),
        el('p', '', [
          unlocked
            ? `Record : ${Math.round(rec.bestScore)} pts · J${rec.bestDay}`
            : `Verrouillé (J${MAP_DEFINITIONS[id].unlockRequirementDay} sur ${MAP_DEFINITIONS[id].unlockOnMap ?? '—'})`,
        ]),
      );
      if (unlocked) {
        card.append(
          button('Lancer', 'btn primary', () => {
            this.selectedMap = id;
            this.startGame(id);
          }),
        );
      }
      list.append(card);
    }

    screen.append(list, button('Retour', 'btn ghost back-btn', () => this.navigate('menu')));
  }

  private startGame(mapId: MapId, opts?: { fresh?: boolean }): void {
    if (this.game) {
      this.game.destroy(false);
      this.game = null;
      this.hud = null;
    }
    this.gameOverPending = null;
    removeModals(this.root);
    if (opts?.fresh) GameController.clearAutosave();
    this.selectedMap = mapId;
    this.screen = 'game';
    this.render();
  }

  private renderGame(): void {
    const screen = mountEmptyScreen(this.root, 'game-screen');
    const canvas = el('canvas', 'game-canvas') as HTMLCanvasElement;
    screen.append(canvas);

    const autosave = GameController.loadAutosave();
    let useSave: string | null = null;
    if (autosave) {
      try {
        const data = JSON.parse(autosave);
        const st = data.state;
        if (st?.mapId === this.selectedMap && !st.gameOver) {
          useSave = autosave;
        } else if (st?.gameOver) {
          // Dead run left in storage — don't resume a stuck game-over with no modal.
          GameController.clearAutosave();
        }
      } catch {
        useSave = null;
      }
    }

    this.hud = createGameHud(
      () => this.game?.togglePause(),
      (s) => this.game?.setSpeed(s),
      (t) => this.game?.setTool(t),
      () => this.openEscapeMenu(),
    );
    screen.append(this.hud.root);

    this.game = new GameController(
      canvas,
      this.selectedMap,
      this.profile.upgrades,
      this.settings,
      this.audio,
      this.api,
      {
        onHudUpdate: () => this.syncHud(),
        onWeekReward: (week) => {
          const offers = this.game!.sim.state.rewardOffers;
          if (offers) {
            showRewardModal(this.root, offers, week, (i) => {
              this.game?.chooseReward(i);
              // Next frame: ensure no sticky pointer after modal click
              requestAnimationFrame(() => this.game?.releasePointerState());
            });
          }
        },
        onGameOver: (day, score) => {
          this.presentGameOver(day, score);
        },
        onTutorialDone: () => {
          this.profile.tutorialDone = true;
          saveProfile(this.profile);
          clearTutorial(this.root);
        },
        // Keyboard ESC is handled once at App level; keep callback for HUD ☰.
        onEscapeMenu: () => this.openEscapeMenu(),
      },
      useSave,
    );

    if (this.game.showTutorial) {
      showTutorialToast(
        this.root,
        'Reliez le générateur au culot (bas) de l\'ampoule — diagonales OK. Plusieurs câbles sur un générateur partagent sa puissance.',
      );
    }

    // Resume mid-reward if the save still has offers.
    if (this.game.sim.state.waitingForReward && this.game.sim.state.rewardOffers) {
      const week = this.game.sim.state.week;
      const offers = this.game.sim.state.rewardOffers;
      showRewardModal(this.root, offers, week, (i) => {
        this.game?.chooseReward(i);
        requestAnimationFrame(() => this.game?.releasePointerState());
      });
    } else if (this.game.sim.state.waitingForReward) {
      this.game.sim.state.waitingForReward = false;
      this.game.sim.resume();
    }

    this.game.start();
    this.syncHud();
  }

  private presentGameOver(day: number, score: number): void {
    if (this.gameOverPending || this.root.querySelector('.gameover-modal')) return;
    const { fragmentsEarned } = this.awardRun(day, score);
    GameController.clearAutosave();
    this.gameOverPending = { day, score, fragments: fragmentsEarned };
    // Keep the board readable — no heavy canvas veil during admire.
    if (this.game) this.game.renderer.overlay.gameOverFade = 0;
    showGameOverAdmireBar(this.root, { day, score }, () => this.openGameOverResults());
  }

  private openGameOverResults(): void {
    if (!this.gameOverPending) return;
    if (this.root.querySelector('.gameover-modal')) return;
    const data = this.gameOverPending;
    showGameOverModal(this.root, data, {
      replay: () => {
        this.gameOverPending = null;
        removeModals(this.root);
        this.startGame(this.selectedMap, { fresh: true });
      },
      shop: () => this.navigate('shop', { keepSave: false }),
      maps: () => this.navigate('maps', { keepSave: false }),
      menu: () => this.navigate('menu', { keepSave: false }),
    });
  }

  private openEscapeMenu(): void {
    if (!this.game) return;
    // First ESC while drawing cancels the cable; next press opens pause.
    if (this.game.cancelActiveDraw()) return;
    if (this.game.sim.state.gameOver) {
      // Admire → results on ESC / ☰; results modal already open → ignore.
      if (this.root.querySelector('.gameover-modal')) return;
      if (this.gameOverPending) {
        this.openGameOverResults();
        return;
      }
      this.presentGameOver(this.game.sim.state.day, this.game.sim.state.score);
      return;
    }
    // Don't cover an open weekly reward picker.
    if (
      this.game.sim.state.waitingForReward &&
      this.root.querySelector('.modal-overlay:not(.pause-overlay)')
    ) {
      return;
    }
    // ESC toggles pause menu closed only when the main pause panel is open
    // (not when nested settings are showing — settings has its own Retour).
    if (this.root.querySelector('.pause-overlay .pause-modal')) {
      const title = this.root.querySelector('.pause-modal .modal-title')?.textContent;
      if (title === 'Réglages') {
        removeModals(this.root);
        this.showPausePanel();
        return;
      }
      removeModals(this.root);
      if (this.game.sim.state.paused && !this.game.sim.state.waitingForReward) {
        this.game.sim.resume();
      }
      return;
    }
    this.showPausePanel();
  }

  private showPausePanel(): void {
    if (!this.game || this.game.sim.state.gameOver) return;
    this.game.sim.pause();
    const s = this.game.sim.state;
    const weeks = Math.floor(s.day / 7);
    const fragmentsIfAbandon = computeFragments(weeks, Math.round(s.score));
    showPauseMenu(
      this.root,
      { fragmentsIfAbandon },
      {
        continue: () => {
          if (!this.game?.sim.state.waitingForReward) this.game?.sim.resume();
        },
        abandon: () => this.settleRunAndLeave('menu'),
        settings: () => this.openPauseSettings(),
      },
    );
  }

  private openPauseSettings(): void {
    showInGameSettings(
      this.root,
      this.settings,
      (next) => {
        this.settings = { ...this.settings, ...next };
        saveSettings(this.settings);
        this.audio.updateSettings(this.settings);
        this.game?.renderer.updateSettings(this.settings);
      },
      () => this.showPausePanel(),
    );
  }

  private syncHud(): void {
    if (!this.game || !this.hud) return;
    const s = this.game.sim.state;
    updateHud(this.hud, {
      day: s.day,
      week: s.week,
      score: Math.round(s.score),
      cables: s.cablesRemaining,
      relays: s.relaysRemaining,
      switches: s.switchesRemaining,
      teleporters: s.teleporterPairsRemaining,
      pendingTeleporter: !!s.pendingTeleporterId,
      paused: s.paused,
      speed: s.speed,
      tool: this.game.tool,
    });
  }

  private renderShopScreen(): void {
    const screen = mountEmptyScreen(this.root, 'shop-screen');
    renderShop(
      screen,
      this.profile,
      async (id, cost) => {
        if (this.profile.fragments < cost) return;
        const ok = await this.api.purchaseUpgrade(id, cost);
        if (ok) {
          this.profile = this.api.getProfile();
          this.renderShopScreen();
        }
      },
      () => this.navigate('menu'),
    );
  }

  private async renderLeaderboard(): Promise<void> {
    const screen = mountEmptyScreen(this.root, 'leaderboard-screen');
    const mapLabels: Record<MapId, string> = {
      core: 'Noyau',
      ruins: 'Ruines',
      magnetic: 'Magnétique',
    };
    let map: MapId = 'core';

    const topbar = el('div', 'screen-topbar');
    topbar.append(
      button('← Retour', 'btn ghost shop-back-top', () => this.navigate('menu')),
      el('div', 'shop-top-meta', [el('h1', 'screen-title', ['Classement'])]),
    );

    const mapTabs = el('div', 'pill-tabs map-tabs');
    const sortTabs = el('div', 'pill-tabs sort-tabs');
    const body = el('div', 'leaderboard-list');

    const refreshTabStyles = () => {
      mapTabs.querySelectorAll('.pill-tab').forEach((btn) => {
        const id = (btn as HTMLElement).dataset.map as MapId;
        btn.classList.toggle('active', id === map);
      });
      sortTabs.querySelectorAll('.pill-tab').forEach((btn) => {
        const id = (btn as HTMLElement).dataset.sort;
        btn.classList.toggle('active', id === this.lbTab);
      });
    };

    const load = async () => {
      refreshTabStyles();
      body.replaceChildren(el('p', 'muted', ['Chargement…']));
      const entries = await this.api.fetchLeaderboard(map, this.lbTab);
      body.replaceChildren();
      if (!entries.length) {
        body.append(el('p', 'muted', ['Aucune entrée — jouez une partie !']));
        return;
      }
      for (const e of entries) {
        const row = el('div', 'lb-row');
        row.append(
          el('span', 'lb-rank', [`#${e.rank}`]),
          el('span', 'lb-name', [e.username]),
          el('span', 'lb-val', [
            this.lbTab === 'score'
              ? `${Math.round(e.score)} pts`
              : `Jour ${e.day ?? 0}`,
          ]),
        );
        body.append(row);
      }
    };

    for (const m of ['core', 'ruins', 'magnetic'] as MapId[]) {
      const btn = button(mapLabels[m], `pill-tab ${map === m ? 'active' : ''}`, () => {
        map = m;
        void load();
      });
      btn.dataset.map = m;
      mapTabs.append(btn);
    }

    for (const [id, label] of [
      ['score', 'Score'],
      ['day', 'Jour'],
    ] as const) {
      const btn = button(label, `pill-tab ${this.lbTab === id ? 'active' : ''}`, () => {
        this.lbTab = id;
        void load();
      });
      btn.dataset.sort = id;
      sortTabs.append(btn);
    }

    const filters = el('div', 'lb-filters');
    filters.append(
      el('p', 'lb-filter-label', ['Carte']),
      mapTabs,
      el('p', 'lb-filter-label', ['Tri']),
      sortTabs,
    );

    screen.append(topbar, filters, body);
    await load();
  }

  private renderProfile(): void {
    const screen = mountEmptyScreen(this.root, 'profile-screen');
    const mapLabels: Record<MapId, string> = {
      core: 'Noyau',
      ruins: 'Ruines',
      magnetic: 'Magnétique',
    };
    const name = this.profile.username ?? 'Invité';
    const initial = name.slice(0, 1).toUpperCase();

    const topbar = el('div', 'screen-topbar');
    topbar.append(
      button('← Retour', 'btn ghost shop-back-top', () => this.navigate('menu')),
      el('div', 'shop-top-meta', [el('h1', 'screen-title', ['Profil'])]),
    );

    const hero = el('div', 'profile-hero');
    const avatar = el('div', 'profile-avatar', [initial]);
    const meta = el('div', 'profile-hero-meta');
    meta.append(
      el('h2', 'profile-name', [name]),
      el('p', 'profile-id', [
        this.profile.loggedIn ? 'Compte connecté' : `ID local · ${this.profile.guestId}`,
      ]),
      el('p', 'profile-fragments', [`✦ ${this.profile.fragments} fragments`]),
    );
    hero.append(avatar, meta);

    const records = el('div', 'profile-records');
    records.append(el('h3', 'profile-section-title', ['Records par carte']));
    const grid = el('div', 'profile-record-grid');
    for (const id of ['core', 'ruins', 'magnetic'] as MapId[]) {
      const r = this.profile.records[id];
      const unlocked = this.mapUnlocked(id);
      const card = el('div', `profile-record-card ${unlocked ? '' : 'locked'}`.trim());
      card.append(
        el('h4', '', [mapLabels[id]]),
        el('p', 'profile-record-score', [
          unlocked ? `${Math.round(r.bestScore)} pts` : 'Verrouillé',
        ]),
        el('p', 'profile-record-day', [
          unlocked ? (r.bestDay > 0 ? `Meilleur jour J${r.bestDay}` : 'Pas encore joué') : '—',
        ]),
      );
      grid.append(card);
    }
    records.append(grid);

    screen.append(topbar, hero, records);
  }

  private renderSettings(): void {
    const screen = mountEmptyScreen(this.root, 'settings-screen');
    screen.append(el('h1', 'screen-title', ['Réglages']));

    const mkRange = (label: string, key: 'musicVolume' | 'sfxVolume', max = 1) => {
      const wrap = el('label', 'setting-row');
      wrap.append(el('span', '', [label]));
      const input = el('input') as HTMLInputElement;
      input.type = 'range';
      input.min = '0';
      input.max = String(max);
      input.step = '0.05';
      input.value = String(this.settings[key]);
      input.addEventListener('input', () => {
        this.settings[key] = Number(input.value);
        saveSettings(this.settings);
        this.audio.updateSettings(this.settings);
      });
      wrap.append(input);
      return wrap;
    };

    screen.append(
      mkRange('Musique', 'musicVolume'),
      mkRange('Effets', 'sfxVolume'),
    );

    const toggles: [string, keyof GameSettings][] = [
      ['Particules', 'particles'],
      ['Lueur', 'glow'],
      ['Grille', 'showGrid'],
      ['Symboles daltoniens', 'colorblindSymbols'],
    ];
    for (const [label, key] of toggles) {
      const row = el('label', 'setting-row');
      const input = el('input') as HTMLInputElement;
      input.type = 'checkbox';
      input.checked = !!this.settings[key];
      input.addEventListener('change', () => {
        this.settings = { ...this.settings, [key]: input.checked };
        saveSettings(this.settings);
        this.game?.renderer.updateSettings(this.settings);
      });
      row.append(input, el('span', '', [label]));
      screen.append(row);
    }

    screen.append(button('Retour', 'btn ghost back-btn', () => this.navigate('menu')));
  }
}
